import PageHead from "../../../layout/PageHead";
import {
  ProjectOutlined,
  CloseOutlined,
  CheckOutlined,
  PlusOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  NOTIFICATION_TYPES,
  SECTIONS,
} from "../../../../config/constants";
import {
  PREFERENCE_OBJECT_TYPES
} from "../../../../config/consts/appsettings";
import { useEffect, useState } from "react";
import "./ApplicationSettings.css";
import Datatable from "../../../shared/components/Datatable";
import { ColumnsType } from "antd/lib/table";
import ApplicationService from "../../../../services/application/ApplicationService";
import {
  AutoComplete,
  Button,
  Drawer,
  DrawerProps,
  Form,
  Input,
  Select,
  Space,
  Tabs,
} from "antd";
import UsersService from "../../../../services/admin/UsersService";
import UserGroupService from "../../../../services/admin/UserGroupsService";
import NotificationsUtil from "../../../shared/utils/NotificationsUtil";
import { ROUTES } from "../../../../config/consts/routes";
import TabPane = Tabs.TabPane;
import GroupCodesService from "../../../../services/groupcodes/GroupCodesService";
import TablesService from "../../../../services/tables/TablesService";
import { DefaultOptionType } from "antd/lib/select";
import { cp } from "fs";
import { Preference } from "../../../../models/application-settings/Preference";
import { PreferenceKey } from "../../../../models/application-settings/PreferenceKey";
import { Component } from "../../../../models/application-settings/Component";
import { GroupCode } from "../../../../models/groupcodes/GroupCode";
import { Entity } from "../../../../models/entities/Entity";
import {PreferenceView} from "../../../../models/application-settings/PreferenceView";
import {PERMISSION_METHODS_ARRAY} from "../../../../config/consts/permissions";

const routes = [
  {
    path: ROUTES.general.main.route,
    breadcrumbName: SECTIONS.general.name,
  },
  {
    breadcrumbName: SECTIONS.administration.name,
  },
  {
    path: ROUTES.administration.applicationSettings.route,
    breadcrumbName: ROUTES.administration.applicationSettings.name,
  },
];

function ApplicationSettings() {
  const { t, i18n } = useTranslation("translations");
  const [groupCodes, setGroupCodes] = useState<GroupCode[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [operations, setOperations] = useState<any>([]);
  const [selectedGroupCode, setSelectedGroupCode] = useState<any>();
  const [selectedEntity, setSelectedEntity] = useState<any>();
  const [selectedOperation, setSelectedOperation] = useState<any>();
  const [data, setData] = useState<PreferenceView[]>([]);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [placement, setPlacement] =
      useState<DrawerProps["placement"]>("bottom");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);

  const [selectedId, setSelectedId] = useState<any>(undefined);

  const [components, setComponents] = useState<Component[]>([]);
  const [preferenceKeys, setPreferenceKeys] = useState<PreferenceKey[]>([]);
  const [objects, setObjects] = useState<DefaultOptionType[]>([]);

  const [drawerTitle, setDrawerTitle] = useState<string>("");
  const [drawerAction, setDrawerAction] = useState<RowAction>();

  const [activeTab, setActiveTab] = useState("application-settings");

  const [users, setUsers] = useState<any>([]);
  const [userGroups, setUserGroups] = useState<any>([]);
  const [preferenceValues, setPreferenceValues] = useState<Preference[]>();

  //form
  const [form] = Form.useForm<Form>();
  const [isValidForm, setIsValidForm] = useState(false);

  interface Form {
    componentId: number;
    key: string;
    objectId: number;
    object: string;
    value: string;
  }

  // Regular expression to match alphanumeric characters, underscores, and hyphens, but no spaces
  const inputRegex = /^[a-zA-Z0-9_-]+$/;

  enum RowAction {
    View,
    Edit,
    Create,
  }

  useEffect(() => {
    document.title = ROUTES.administration.applicationSettings.name;
  }, []);

  useEffect(() => {
    ApplicationService.getComponents().then((response) => {
      const data = response.data;
      setComponents(data);
    });
    ApplicationService.getPreferenceKeys().then((response) => {
      const data = response.data;
      setPreferenceKeys(data);
    });
    UserGroupService.getAll().then((response) => {
      const data = response.data;
      setUserGroups(data);
    })
    UsersService.getAll().then((response) => {
      const data = response.data;
      setUsers(data);
    })
    GroupCodesService.getAll().then((response) => {
      const data = response.data;
      setGroupCodes(data);
    });
    TablesService.getAllEntities().then((response) => {
      const data = response.data;
      setEntities(data);
    });
  }, []);

  useEffect(() => {
    if (isTableLoading) {
      ApplicationService.getPreferences().then((response) => {
        const responseData = response.data;
        setData(
            responseData.map(x => ({
              componentId: x.componentId,
              componentName: x.componentName,
              id: x.id,
              preferenceKey: x.key,
              object: x.object,
              objectId: x.objectId,
              objectName: x.object === "Entity" && x.objectId ? entities.find(y => y.id == x.objectId)?.name : "",
              value: x.value,
            }))
        );
      }).catch((error: any) => {
        NotificationsUtil.openNotificationDialog({
          description: error.response.data,
          type: NOTIFICATION_TYPES.ERROR,
          logError: true,
        });
      }).finally(() => {
        setIsTableLoading(false);
      });
    }
  }, [isTableLoading]);

  const columns: ColumnsType<PreferenceView> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: "60px",
    },
    {
      title: "Component",
      dataIndex: "componentName",
      key: "componentName",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) =>  a.componentName.localeCompare(b.componentName) 
    },
    {
      title: "Key",
      dataIndex: "preferenceKey",
      key: "preferenceKey",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.preferenceKey ? a.preferenceKey.localeCompare(b.preferenceKey ?? "") : "".localeCompare(b.preferenceKey ?? ""),
    },
    {
      title: "Object",
      dataIndex: "object",
      key: "object",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.object ? a.object.localeCompare(b.object ?? "") : "".localeCompare(b.object ?? ""),
    },
    {
      title: "Object Name",
      dataIndex: "objectName",
      key: "objectName",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.objectName ? a.objectName.localeCompare(b.objectName ?? "") : "".localeCompare(b.objectName ?? ""),
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.value ? a.value.localeCompare(b.value ?? "") : "".localeCompare(b.value ?? ""),
    },
  ];

  const scroll = { y: "calc(100vh - 320px)" };

  const onEditAppplicationSettings = (record: Preference) => {
    updateObjectSelectOptions(record.object);
    showDrawer(RowAction.Edit);
    form.setFieldsValue(record);
    setSelectedId(record.id);
  };

  const onDoubleClickRow = (record: Preference) => {
    updateObjectSelectOptions(record.object);
    showDrawer(RowAction.View);
    form.setFieldsValue(record);
    setSelectedId(record.id);
  };
  const onClose = () => {
    setOpenDrawer(false);
    setObjects([]);
    setSelectedId(undefined);
    setIsValidForm(false);
    form.resetFields();
  };

  const onSavePreference = () => {
    const formValues = form.getFieldsValue();
    
    setLoadingButton(true);
    if (selectedId) {
      ApplicationService.updatePreference(selectedId, formValues).then((response: any) => {
        NotificationsUtil.openNotificationDialog({
          message: "Save successful",
          description: "Application preference successfully saved",
          placement: "bottomRight",
          type: NOTIFICATION_TYPES.SUCCESS,
          logError: false,
        });
        onClose();
        setIsTableLoading(true);
      }).catch((error: any) => {
        NotificationsUtil.openNotificationDialog({
          description: error.response.data,
          type: NOTIFICATION_TYPES.ERROR,
          logError: true,
        });
      }).finally(() => {
        setLoadingButton(false);
      });
    } else {
      ApplicationService.createPreference(formValues).then((response: any) => {
        NotificationsUtil.openNotificationDialog({
          message: "Save successful",
          description: "Application preference successfully saved",
          placement: "bottomRight",
          type: NOTIFICATION_TYPES.SUCCESS,
          logError: false,
        });
        onClose();
        setIsTableLoading(true);
      }).catch((error: any) => {
        NotificationsUtil.openNotificationDialog({
          description: error.response.data,
          type: NOTIFICATION_TYPES.ERROR,
          logError: true,
        });
      }).finally(() => {
        setLoadingButton(false);
      });
    }
  };

  const onValuesChange = (changedValues: Partial<Form>) => {
    updateObjectSelectOptions(changedValues.object);
    updateValueSelectOptions(changedValues.key);
  };

  const updateValueSelectOptions = (key: string) => {
    if (key) {
      setPreferenceValues([]);
      ApplicationService.getPreferenceValues(key).then((response) => {
        const data = response.data;
        setPreferenceValues(data);
      });
    }
  };

  const updateObjectSelectOptions = (object: string) => {
    if (object) {
      form.setFieldsValue({ objectId: undefined });
    }

    switch(object) {
      case "Application":
        setObjects([]);
        break;
      case "UserGroup":
        console.log("usergroups", userGroups);
        setObjects(userGroups.map((g) => { return { label: g.name, value: g.id } }));
        break;
      case "User":
        console.log("users", users);
        setObjects(users.map((u) => { return { label: u.userName, value: u.id } }));
        break;
      case "Entity":
        setObjects(entities.map((e) => { return { label: e.name, value: e.id } }));
        break;
      case "GroupCode":
        setObjects(groupCodes.map((g) => { return { label: g.code, value: g.id } }));
        break;
    }
  };

  const onDeleteAppplicationSettings = (id: number) => {
    ApplicationService.deletePreference(id)
        .then((response: any) => {
          setIsTableLoading(true);
          NotificationsUtil.openNotificationDialog({
            description: t(
                "Page.Operation.AppplicationSettings.Notifications.Preference.DeleteSuccessDescription",
                {
                  lngs: i18n.languages,
                  defaultValue: "Preference deleted successfully.",
                }
            )!,
            type: NOTIFICATION_TYPES.SUCCESS,
            logError: false,
          });
        })
        .catch((error: any) => {
          NotificationsUtil.openNotificationDialog({
            description: error.response.data,
            type: NOTIFICATION_TYPES.ERROR,
            logError: true,
          });
        });
  };

  const showDrawer = (action: RowAction) => {
    setDrawerAction(action);
    switch (action) {
      case RowAction.View:
        setDrawerTitle("Row details");
        break;
      case RowAction.Create:
        setDrawerTitle("New preference");
        break;
      case RowAction.Edit:
        setDrawerTitle("Edit preference");
        break;
      default:
        break;
    }
    setOpenDrawer(true);
  };

  const handleNewPreferenceButtonClick = () => {
    showDrawer(RowAction.Create);
    setSelectedId(undefined);
  }

  const onFieldsChange = () => {
    setIsValidForm(form.getFieldsError().filter(({ errors }) => errors.length).length === 0);
  }

  const applicationSettingsActionButton = (
      <Button
          type={"primary"}
          onClick={handleNewPreferenceButtonClick}
          className="actionButton"
      >
        <PlusOutlined />
        New preference
      </Button>
  );

  return (
      <div>
        <PageHead
            title={t(
                "Page.Administration.RoleManagement.Headers.ApplicationSettings",
                {
                  lngs: i18n.languages,
                  defaultValue: "Application Settings",
                }
            )}
            icon={<ProjectOutlined />}
            routes={routes}
            groupCodes={groupCodes}
            entities={entities}
            operations={operations}
            selectedGroupCode={selectedGroupCode}
            selectedEntity={selectedEntity}
            selectedOperation={selectedOperation}
            setSelectedGroupCode={setSelectedGroupCode}
            setSelectedEntity={setSelectedEntity}
            setSelectedOperation={setSelectedOperation}
            setSelectGroupCodeDisabled={true}
            setSelectEntityDisabled={true}
            setSelectOperationDisabled={true}
            mainAction={applicationSettingsActionButton}
            otherActions={[]}
            setActionsDisabled={false}
        />
        <div className="card-container">
          <Tabs
              type="card"
              defaultActiveKey="group-management"
              onChange={(tab) => setActiveTab(tab)}
              style={{ height: "100%" }}
          >
            <TabPane
                tab={t("Page.Administration.ApplicationSettings.Headers.ApplicationSettings", {
                  lngs: i18n.languages,
                  defaultValue: "Application Settings",
                })}
                key="application-settings"
            >
              <Datatable
                  scroll={scroll}
                  tableColumns={columns}
                  data={data}
                  setData={setData}
                  isTableLoading={isTableLoading}
                  otherActions={[]}
                  otherRowEnabled={false}
                  editRowEnabled={true}
                  copyRowEnabled={false}
                  deleteRowEnabled={true}
                  onEditFunc={onEditAppplicationSettings}
                  onCopyFunc={undefined}
                  onDeleteFunc={onDeleteAppplicationSettings}
                  onDoubleClickRow={onDoubleClickRow}
                  selectable={false}
                  multiSelect={false}
                  selectedRows={[]}
                  setSelectedRows={undefined}
                  paginationDisabled={true}
              ></Datatable>
              <Drawer
                  width={"400px"}
                  getContainer={false}
                  style={{ position: "absolute" }}
                  title={drawerTitle}
                  placement={placement}
                  onClose={onClose}
                  visible={openDrawer}
                  closable={false}
                  height={"550px"}
                  extra={
                    <Space>
                      {RowAction?.View === drawerAction && (
                          <>
                            <Button
                                onClick={() => showDrawer(RowAction.Edit)}
                                icon={<EditOutlined />}
                                className={"active-btn"}
                                size="small"
                            >
                              Edit
                            </Button>
                            <Button
                                type="ghost"
                                onClick={onClose}
                                icon={<CloseOutlined />}
                                size="small"
                            />
                          </>
                      )}
                      {RowAction?.Create === drawerAction && (
                          <>
                            <Button
                                disabled={!isValidForm}
                                onClick={onSavePreference}
                                icon={<CheckOutlined />}
                                size="small"
                                loading={loadingButton}
                            >
                              Save
                            </Button>
                            <Button
                                type="ghost"
                                onClick={onClose}
                                icon={<CloseOutlined />}
                                size="small"
                            />
                          </>
                      )}
                      {RowAction?.Edit === drawerAction && (
                          <>
                            <Button
                                onClick={onSavePreference}
                                icon={<CheckOutlined />}
                                size="small"
                                loading={loadingButton}
                            >
                              Update
                            </Button>
                            <Button
                                type="ghost"
                                onClick={onClose}
                                icon={<CloseOutlined />}
                                size="small"
                            />
                          </>
                      )}
                    </Space>
                  }
              >
                <Form
                    disabled={RowAction.View === drawerAction ? true : false}
                    form={form}
                    onFieldsChange={onFieldsChange}
                    onValuesChange={onValuesChange}
                    initialValues={{ "object": "Application" }}
                    layout="vertical">
                  <Form.Item
                      label="Component:"
                      name="componentId"
                      rules={[{ required: true, message: "Please select a component" }]}
                  >
                    <Select
                        allowClear
                        showSearch
                        style={{ width: "100%" }}
                        placeholder="Please select"
                        filterSort={(optionA, optionB) =>
                            (optionA!.label as unknown as string)
                                .toLowerCase()
                                .localeCompare(
                                    (optionB!.label as unknown as string).toLowerCase()
                                )
                        }
                        filterOption={(input, option) =>
                            (option?.label ?? "")
                                .toString()
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        options={components?.map(
                            (u) => {
                              return {
                                label: u.name,
                                value: u.id,
                              };
                            }
                        )}
                    />
                  </Form.Item>
                  <Form.Item
                      label="Key:"
                      name="preferenceKey"
                  >
                    <AutoComplete
                        allowClear
                        showSearch
                        style={{ width: "100%" }}
                        placeholder="Please select"
                        filterSort={(optionA, optionB) =>
                            (optionA!.label as unknown as string)
                                .toLowerCase()
                                .localeCompare(
                                    (optionB!.label as unknown as string).toLowerCase()
                                )
                        }
                        filterOption={(input, option) =>
                            (option?.label ?? "")
                                .toString()
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        options={preferenceKeys.map(
                            (u) => {
                              return {
                                label: u.value,
                                value: u.value,
                              };
                            }
                        )}
                    />
                  </Form.Item>
                  <Form.Item
                      label="Object:"
                      name="object"
                      rules={[{ required: true, message: "Please select an object" }]}
                  >
                    <Select
                        style={{ width: "100%" }}
                        placeholder="Please select"
                        filterSort={(optionA, optionB) =>
                            (optionA!.label as unknown as string)
                                .toLowerCase()
                                .localeCompare(
                                    (optionB!.label as unknown as string).toLowerCase()
                                )
                        }
                        filterOption={(input, option) =>
                            (option?.label ?? "")
                                .toString()
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        options={PREFERENCE_OBJECT_TYPES.map(
                            (u) => {
                              return {
                                label: u,
                                value: u,
                              };
                            }
                        )}
                    />
                  </Form.Item>
                  <Form.Item label="Object Name:"
                             name="objectId">
                    <Select
                        disabled={form.getFieldValue("object") === "Application"}
                        style={{ width: "100%" }}
                        placeholder="Any"
                        allowClear
                        filterSort={(optionA, optionB) =>
                            (optionA!.label as unknown as string)
                                .toLowerCase()
                                .localeCompare(
                                    (optionB!.label as unknown as string).toLowerCase()
                                )
                        }
                        filterOption={(input, option) =>
                            (option?.label ?? "")
                                .toString()
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        options={objects}
                    />
                  </Form.Item>
                  <Form.Item
                      label="Value:"
                      name="value"
                      rules={[{ required: true, message: "Please enter a value" }]}
                      dependencies={['preferenceKey']}
                  >
                    {(form.getFieldValue("preferenceKey") === "Permission_Default_GroupCode" ||
                    form.getFieldValue("preferenceKey") === "Permission_Default_Entity")? (
                        <Select
                            style={{ width: "100%" }}
                            allowClear
                            showSearch
                            placeholder="Please select"
                            filterSort={(optionA, optionB) =>
                                (optionA!.label as unknown as string)
                                    .toLowerCase()
                                    .localeCompare((optionB!.label as unknown as string).toLowerCase())
                            }
                            filterOption={(input, option) =>
                                (option?.label ?? "")
                                    .toString()
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                            options={PERMISSION_METHODS_ARRAY
                                .filter(x => x != "NOT ASSIGNED")
                                .map(pm => {
                                  return {
                                    label: pm,
                                    value: pm,
                                  }
                                })
                                .sort((a,b) => a.value.localeCompare(b.value))
                            }
                        />
                    ) : (
                        <AutoComplete
                            style={{ width: "100%" }}
                            allowClear
                            placeholder="Enter value"
                            showSearch
                            filterSort={(optionA, optionB) =>
                                (optionA!.label as unknown as string)
                                    .toLowerCase()
                                    .localeCompare((optionB!.label as unknown as string).toLowerCase())
                            }
                            filterOption={(input, option) =>
                                (option?.label ?? "")
                                    .toString()
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                            options={preferenceValues?.map((x) => ({ label: x.value, value: x.value }))}
                        />
                    )}
                  </Form.Item>
                </Form>
              </Drawer>
            </TabPane>
          </Tabs>
        </div>
      </div>
  );
}

export default ApplicationSettings;
